import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormInputField,
  FormSelectField,
  type SelectOption,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ICompany } from "@models";
import {
  COMPANY_KEY,
  useCountries,
  useLanguages,
  useUpdateCompany,
  withCurrentRegionalOption,
} from "@query";
import { APP_BASE_PATH, base64ToDataUrl, getIconUrl } from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, CloudUpload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const createEditCompanyFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("hub.administer.add_user_required")),
    skpCustId: z.string().optional(),
    authType: z.string().min(1, t("hub.administer.add_user_required")),
    language: z.string().min(1, t("hub.administer.add_user_required")),
    serviceStreetAddress: z
      .string()
      .min(1, t("hub.administer.add_user_required")),
    serviceCountry: z.string().min(1, t("hub.administer.add_user_required")),
    serviceCity: z.string().min(1, t("hub.administer.add_user_required")),
    serviceState: z.string().min(1, t("hub.administer.add_user_required")),
    servicePostalCode: z.string().min(1, t("hub.administer.add_user_required")),
  });

type EditCompanyFormData = z.infer<
  ReturnType<typeof createEditCompanyFormSchema>
>;

const BASE_AUTH_OPTIONS: SelectOption[] = [
  { value: "IM Okta", label: "IM Okta" },
  { value: "SAML", label: "SAML" },
  { value: "OIDC", label: "OIDC" },
];

function getAuthOptions(companyAuthType?: string): SelectOption[] {
  if (!companyAuthType) return BASE_AUTH_OPTIONS;
  const exists = BASE_AUTH_OPTIONS.some((o) => o.value === companyAuthType);
  if (exists) return BASE_AUTH_OPTIONS;
  return [
    { value: companyAuthType, label: companyAuthType },
    ...BASE_AUTH_OPTIONS,
  ];
}

const LOGO_ACCEPT = "image/jpeg,image/png,image/svg+xml";
const LOGO_MAX_SIZE = 2 * 1024 * 1024; // 2MB

interface IEditCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: ICompany | undefined;
  companyUuid: string | undefined;
}

export function EditCompanyDialog({
  open,
  onOpenChange,
  company,
  companyUuid,
}: IEditCompanyDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const editCompanyFormSchema = createEditCompanyFormSchema(t);

  const { data: languages } = useLanguages(APP_BASE_PATH, { enabled: open });
  const { data: countries } = useCountries(APP_BASE_PATH, { enabled: open });

  const languageOptions = useMemo(
    () => withCurrentRegionalOption(languages, company?.language),
    [languages, company?.language],
  );
  const countryOptions = useMemo(
    () => withCurrentRegionalOption(countries, company?.serviceCountry),
    [countries, company?.serviceCountry],
  );

  const { mutate: updateCompany, isPending } = useUpdateCompany(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
      },
    },
  );

  const computedValues = useMemo<EditCompanyFormData | undefined>(() => {
    if (!company) return undefined;
    return {
      name: company.name ?? "",
      skpCustId: company.skpCustId ?? "",
      authType: company.authType ?? "",
      language: company.language ?? "en",
      serviceStreetAddress: company.serviceStreetAddress ?? "",
      serviceCountry: company.serviceCountry ?? "",
      serviceCity: company.serviceCity ?? "",
      serviceState: company.serviceState ?? "",
      servicePostalCode: company.servicePostalCode ?? "",
    };
  }, [company]);

  const form = useForm<EditCompanyFormData>({
    resolver: zodResolver(editCompanyFormSchema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      skpCustId: "",
      authType: "",
      language: "en",
      serviceStreetAddress: "",
      serviceCountry: "",
      serviceCity: "",
      serviceState: "",
      servicePostalCode: "",
    },
    values: open ? computedValues : undefined,
  });

  const { control } = form;

  useEffect(() => {
    if (open) {
      setLogoPreview(null);
      setLogoFile(null);
    }
  }, [open]);

  const handleLogoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleLogoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > LOGO_MAX_SIZE) return;
      const validTypes = ["image/jpeg", "image/png", "image/svg+xml"];
      if (!validTypes.includes(file.type)) return;
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
      e.target.value = "";
    },
    [],
  );

  const onSubmit = useCallback(
    async (data: EditCompanyFormData) => {
      if (!companyUuid) return;
      const payload: Partial<ICompany> = {
        name: data.name,
        skpCustId: data.skpCustId || null,
        authType: data.authType,
        language: data.language,
        serviceStreetAddress: data.serviceStreetAddress,
        serviceCountry: data.serviceCountry,
        serviceCity: data.serviceCity,
        serviceState: data.serviceState,
        servicePostalCode: data.servicePostalCode,
      };
      if (logoFile) {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(logoFile);
        });
        payload.logo = base64ToDataUrl(base64);
      }
      updateCompany(payload, {
        onSuccess: () => {
          onOpenChange(false);
          toast.add({
            title: t("hub.administer.company_update_success"),
            description: t("hub.administer.company_update_success_description"),
            data: { type: "success" },
          });
        },
        onError: () => {
          toast.add({
            title: t("hub.administer.company_update_error"),
            description: t("hub.administer.company_update_error_description"),
            data: { type: "error" },
          });
        },
      });
    },
    [companyUuid, logoFile, updateCompany, onOpenChange, toast, t],
  );

  const displayLogo =
    logoPreview ?? (company?.logo ? getIconUrl(company.logo) : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className="max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        <DialogHeader className="shrink-0 mb-4">
          <DialogTitle className="text-base font-medium">
            {t("hub.administer.edit_company_details")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex-1 overflow-y-auto min-h-0 px-2 py-1">
          <Form {...form}>
            <form
              id="edit-company-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 text-left">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex shrink-0 flex-col items-center gap-2">
                  <div
                    className="flex size-24 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                    aria-hidden>
                    {displayLogo ? (
                      <img
                        src={displayLogo}
                        alt=""
                        className="size-full rounded-lg object-contain"
                      />
                    ) : (
                      <Building2 className="size-10" />
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={LOGO_ACCEPT}
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleLogoClick}>
                    <CloudUpload className="size-4" />
                    {t("hub.administer.upload_logo")}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {t("hub.administer.logo_file_hint")}
                  </span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormInputField<EditCompanyFormData>
                      control={control}
                      name="name"
                      label={`${t("hub.administer.company_name")}`}
                      placeholder={t("hub.administer.company_name")}
                      description={t(
                        "hub.administer.company_name_description",
                        {
                          defaultValue: "The name of your company.",
                        },
                      )}
                      required
                      disabled={true}
                    />
                    <FormInputField<EditCompanyFormData>
                      control={control}
                      name="skpCustId"
                      label={`${t("hub.administer.skp_customer_id")} (${t("hub.catalog_subscribe.optional_placeholder")})`}
                      placeholder={t("hub.administer.skp_customer_id")}
                      description={t(
                        "hub.administer.skp_customer_id_description",
                        {
                          defaultValue: "Your SKP customer ID.",
                        },
                      )}
                      disabled={true}
                    />
                    <FormSelectField<EditCompanyFormData>
                      control={control}
                      name="authType"
                      label={`${t("hub.administer.authentication_method")} *`}
                      placeholder={t(
                        "hub.administer.add_user_select_placeholder",
                      )}
                      description={t("hub.administer.auth_type_description", {
                        defaultValue:
                          "The authentication method for your company.",
                      })}
                      items={getAuthOptions(company?.authType)}
                      readOnly
                    />
                    <FormSelectField<EditCompanyFormData>
                      control={control}
                      name="language"
                      label={`${t("hub.locale_switch.menu_label")}`}
                      placeholder={t(
                        "hub.administer.add_user_select_placeholder",
                      )}
                      description={t("hub.administer.language_description", {
                        defaultValue: "The default language for your company.",
                      })}
                      items={languageOptions}
                    />
                    <FormInputField<EditCompanyFormData>
                      control={control}
                      name="serviceStreetAddress"
                      label={`${t("hub.administer.street_address")}`}
                      placeholder={t("hub.administer.street_address")}
                      description={t(
                        "hub.administer.street_address_description",
                        {
                          defaultValue: "Your company's street address.",
                        },
                      )}
                      className="col-span-2"
                      required
                    />
                    <FormSelectField<EditCompanyFormData>
                      control={control}
                      name="serviceCountry"
                      label={`${t("hub.administer.country")}`}
                      placeholder={t(
                        "hub.administer.add_user_select_placeholder",
                      )}
                      description={t("hub.administer.country_description", {
                        defaultValue: "Your company's country.",
                      })}
                      items={countryOptions}
                    />
                    <FormInputField<EditCompanyFormData>
                      control={control}
                      name="serviceCity"
                      label={`${t("hub.administer.city")}`}
                      placeholder={t("hub.administer.city")}
                      description={t("hub.administer.city_description", {
                        defaultValue: "Your company's city.",
                      })}
                      required
                    />
                    <FormInputField<EditCompanyFormData>
                      control={control}
                      name="serviceState"
                      label={`${t("hub.administer.state")}`}
                      placeholder={t("hub.administer.state")}
                      description={t("hub.administer.state_description", {
                        defaultValue: "Your company's state.",
                      })}
                      required
                    />
                    <FormInputField<EditCompanyFormData>
                      control={control}
                      name="servicePostalCode"
                      label={`${t("hub.administer.zip_code")}`}
                      placeholder={t("hub.administer.zip_code")}
                      description={t("hub.administer.zip_code_description", {
                        defaultValue: "Your company's postal code.",
                      })}
                      required
                    />
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </DialogBody>
        <DialogFooter className="shrink-0 -mx-6 -mb-6 mt-2 border-t border-border bg-muted/50 p-4 gap-2 sm:gap-0 sm:flex-row sm:justify-end sm:space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("hub.administer.cancel")}
          </Button>
          <Button
            disabled={isPending}
            onClick={() => {
              const formElement = document.getElementById(
                "edit-company-form",
              ) as HTMLFormElement;
              if (formElement) {
                formElement.requestSubmit();
              }
            }}>
            {isPending
              ? t("hub.administer.edit_user_submitting")
              : t("hub.administer.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
